import classNames from "classnames"
import React from "react"

import { Title3 } from "@bounce/design-system"
import { useTranslation } from "@bounce/i18n"
import { Button } from "@bounce/web-components"

import { StoreList } from "./StoreList"
import SafeLink from "@/components/Generic/SafeLink/SafeLink"
import { StoreCard } from "@/components/LuggageStorage/Store/StoreCard/StoreCard"
import { useSectionViewed } from "@/hooks/useSectionViewed"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"
import { useUrl } from "@/libs/router"
import type { PageType } from "@/types"
import { useAppURL } from "@/utils/appURL"

type StoreListSectionProps = {
  className?: string
  from: PageType
  path: string
  searchQuery: string
  sectionViewedMeta?: object
  stores: NearbyStorageSpot[]
  subtitle?: React.ReactNode
  title: React.ReactNode
}

export const StoreListSection = ({
  className,
  from,
  path,
  searchQuery,
  sectionViewedMeta,
  stores,
  subtitle,
  title,
}: StoreListSectionProps) => {
  const { t } = useTranslation()
  const { sectionRef } = useSectionViewed({ name: "StoreListView", meta: sectionViewedMeta })
  const buildAppURL = useAppURL()
  const { getUrl } = useUrl()

  const searchUrl = buildAppURL(getUrl({ path: "/s" }), { query: searchQuery })

  return (
    <section ref={sectionRef} className={classNames("mx-auto flex w-full max-w-screen-lg flex-col", className)}>
      <div className="px-6 lg:px-0">
        <h1 className="title2 break-words">{title}</h1>
        {!!subtitle && <Title3 className="!paragraph1 mt-2 text-neutral-800">{subtitle}</Title3>}
      </div>
      <StoreList from={from} path={path} searchQuery={searchQuery} stores={stores}>
        {store => <StoreCard store={store} />}
      </StoreList>
      <div className="mx-auto mt-12 w-full">
        <Button variant="secondary" as={SafeLink} href={searchUrl} className="mx-auto">
          {t("screen.cityListViewScreen.storeListSection.cta", "See more")}
        </Button>
      </div>
    </section>
  )
}
